import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useToast } from "./use-toast";

export const useDocuments = (userId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Validate userId before using it in the query
  const isValidUserId = userId && typeof userId === 'string' && userId.trim() !== '';

  const { data: documents, isLoading, error } = useQuery({
    queryKey: ['documents', userId],
    queryFn: async () => {
      if (!isValidUserId) {
        console.log("No valid userId provided to useDocuments");
        return [];
      }

      console.log("Fetching documents for user:", userId);
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }

      console.log("Fetched documents:", data);
      return data || [];
    },
    enabled: isValidUserId, // Only run query if we have a valid userId
  });

  useEffect(() => {
    if (!isValidUserId) return;

    console.log("Setting up real-time subscription for documents");
    const channel = supabase
      .channel('document-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'documents',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log("Received document change:", payload);
          await queryClient.invalidateQueries({ queryKey: ['documents', userId] });
        }
      )
      .subscribe((status) => {
        console.log("Subscription status:", status);
      });

    return () => {
      console.log("Cleaning up document subscription");
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  const deleteDocument = async (documentId: string) => {
    if (!isValidUserId || !documentId) {
      console.error('Missing userId or documentId for deletion');
      throw new Error('Missing required parameters for document deletion');
    }

    try {
      console.log("Deleting document:", documentId);
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId)
        .eq('user_id', userId); // Add user_id check for extra security

      if (error) throw error;
      
      await queryClient.invalidateQueries({ queryKey: ['documents', userId] });
      
      toast({
        title: "Dokument slettet",
        description: "Dokumentet er nå fjernet fra oversikten",
      });
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast({
        variant: "destructive",
        title: "Feil ved sletting",
        description: error.message || "Kunne ikke slette dokumentet. Vennligst prøv igjen.",
      });
      throw error;
    }
  };

  return {
    documents,
    isLoading,
    error,
    deleteDocument,
  };
};